import { DashboardLayout } from "components/Dashboard/DashboardLayout";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useTenant } from "hooks/useTenant";
import { Suspense } from "react";

import { ManifestLineItemDetail } from "../../ManifestLineItems/ManifestLineItemDetail";
import { FacilityDetailsTile } from "./FacilityDetailsTile";
import { GeneratorStatusTile } from "./GeneratorStatusTile";
import { ManifestLineItemsTile } from "./ManifestLineItemsTile";
import { WasteFacilityProvider } from "./WasteFacilityContext";
import { WasteFacilityFilters } from "./WasteFacilityFilters";
import { WasteFacilitySkeleton } from "./WasteFacilitySkeleton";
import { WasteGeneratedTile } from "./WasteGenerated/WasteGeneratedTile";
import { WasteShipmentsTile } from "./WasteShipments/WasteShipmentTile";
import { WasteStreamsTile } from "./WasteStreamsTile";

export const WasteFacility = () => {
  const { tenantId, isAmazon } = useTenant();
  const { featureFlags } = useFeatureFlags();
  // Two-layer gate: the card sources from Amazon's WADM feed exclusively, so
  // `isAmazon` is the hard constraint — no other tenant should ever see it
  // even if the flag is mistakenly enabled. The flag then controls rollout
  // WITHIN Amazon (so we can stage the card, run QA, A/B per-facility, etc.).
  const wasteGenerationCardEnabled = isAmazon && featureFlags[FeatureFlagKey.WasteGenerationCard];
  const { selectedId, popIdFromUrl } = useSelectedIdInUrl("wasteLineUnifiedId", {
    prefixSegment: "manifest-line-items",
  });
  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Facilities",
      to: tenantId ? `/o/${tenantId}/waste/facilities` : undefined,
    },
  ]);

  return (
    <Suspense fallback={<WasteFacilitySkeleton />}>
      <WasteFacilityProvider>
        <WasteFacilityFilters />
        <DashboardLayout>
          <FacilityDetailsTile />
          <GeneratorStatusTile />
          <WasteShipmentsTile />
          {/*
            Waste Generated is Amazon-only AND flag-gated. Both must be true.
            Staff override on the flag lets us QA inside the Amazon tenant
            (and only the Amazon tenant) without flipping tenant configs.
          */}
          {wasteGenerationCardEnabled && <WasteGeneratedTile />}
          <WasteStreamsTile />
          <ManifestLineItemsTile />
        </DashboardLayout>
        <ManifestLineItemDetail
          lineItemId={selectedId}
          open={!!selectedId}
          onClose={popIdFromUrl}
        />
      </WasteFacilityProvider>
    </Suspense>
  );
};
